import { useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box, Button, Paper, Stack } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import { useFieldArray, useFormContext } from "react-hook-form";

import { PaymentMethodMappingFormDialog } from "../payment-method-mapping-form-dialog";

export const PaymentMethodMappingSection = ({ outletId, disabled }) => {
  const { control } = useFormContext();

  const {
    fields: paymentMethods,
    append,
    remove,
    update,
  } = useFieldArray({
    control,
    name: "paymentMethods",
  });

  // `null` = closed, `-1` = adding, `>= 0` = editing that row index.
  const [editingIndex, setEditingIndex] = useState(null);
  const isDialogOpen = editingIndex !== null;
  const editingRow = editingIndex >= 0 ? paymentMethods[editingIndex] : null;

  // Prevent selecting a payment method already mapped, but still allow the row
  // currently being edited to keep its own method.
  const excludeIds = paymentMethods
    .filter((_, index) => index !== editingIndex)
    .map((row) => row.paymentMethod?.id)
    .filter(Boolean);

  const handleCloseDialog = () => setEditingIndex(null);

  const handleSave = (row) => {
    if (editingIndex >= 0) {
      update(editingIndex, row);
    } else {
      append(row);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "paymentMethod",
        headerName: "Metode Pembayaran",
        flex: 1,
        sortable: false,
        valueGetter: (_, row) => row.paymentMethod?.name ?? "",
      },
      {
        field: "outletChartOfAccount",
        headerName: "Kas & Bank",
        flex: 2,
        sortable: false,
        valueGetter: (_, row) =>
          row.outletChartOfAccount
            ? `${row.outletChartOfAccount.code} - ${row.outletChartOfAccount.name}`
            : "",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Aksi",
        width: 100,
        getActions: (params) => {
          const index = paymentMethods.findIndex((row) => row.id === params.id);

          return [
            <GridActionsCellItem
              key="edit"
              icon={<EditIcon />}
              label="Ubah"
              onClick={() => setEditingIndex(index)}
              disabled={disabled}
            />,
            <GridActionsCellItem
              key="delete"
              icon={<DeleteIcon />}
              label="Hapus"
              onClick={() => remove(index)}
              color="error"
              disabled={disabled}
            />,
          ];
        },
      },
    ],
    [disabled, paymentMethods, remove],
  );

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEditingIndex(-1)}
            disabled={disabled}
          >
            Tambah Metode Pembayaran
          </Button>
        </Box>
        <DataGrid
          rows={paymentMethods}
          columns={columns}
          getRowId={(row) => row.id}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooter
          localeText={{
            noRowsLabel:
              "Belum ada metode pembayaran. Tambahkan dari tombol di atas.",
          }}
        />
      </Stack>

      {isDialogOpen && (
        <PaymentMethodMappingFormDialog
          key={editingIndex}
          open
          onClose={handleCloseDialog}
          onSave={handleSave}
          outletId={outletId}
          excludeIds={excludeIds}
          initialValue={editingRow}
        />
      )}
    </Paper>
  );
};

PaymentMethodMappingSection.propTypes = {
  outletId: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
};
